// ── Company-ID gate contract (Athena Build 3, frozen) ─────────────────────
// Mirrors metadata.gate_outcome exactly as emitted by Athena's /athena/chat
// SSE stream. Do NOT add fields speculatively — this is a consumed contract,
// not something this repo owns.

// Citation source types are owned by the leads askAica store (the Athena→FE
// contract lives on message.metadata.sources) — reused here so the portfolio
// surface's messages carry the identical shape (RECUR-27369).
import { CitationSource } from '../dashboard/askAica/citationTypes';
import { ReasoningStep } from '../dashboard/askAica/actions';
import { AnswerScope } from './answerScopeTypes';

/** A single candidate offered by an ask_back(kind:"pick") gate. `city` is
 * ALWAYS null on the wire — never render it. */
export interface Candidate {
    org_id: string;
    name: string;
    cin?: string | null;
    city?: string | null;
}

export interface CurrentCompany {
    org_id: string;
    /** Athena may resolve a company by org_id without a display name in some
     * edge cases — the chip falls back to a placeholder rather than rendering
     * blank (see CompanyChip usage in AskAicaInputBox, F-15). */
    name: string | null;
}

/** Runtime values for GateOutcomeType — single source of truth so callers compare
 * against a named constant instead of a hand-typed string literal (human review #16). */
export const GATE_OUTCOME_TYPE = {
    RESOLVED: 'resolved',
    CONTINUE: 'continue',
    PORTFOLIO: 'portfolio',
    COMPANY_LESS: 'company_less',
    ASK_BACK: 'ask_back',
    DECLINE: 'decline',
} as const;

export type GateOutcomeType = (typeof GATE_OUTCOME_TYPE)[keyof typeof GATE_OUTCOME_TYPE];

/** Per-slot resolution state (frozen contract v4 §3 — the wire projection of
 * resolve_reference's five statuses). One slot per name the user wrote.
 *  - `ambiguous`   → interactive radio rows; the user MUST choose. Two-or-more
 *                    candidates is the disambiguation case; EXACTLY ONE is the
 *                    resolver's `confirm` ("did you mean X?") — deliberately not
 *                    a third state, so it needs no third render (v4 §3).
 *  - `resolved`    → locked row naming what the write matched, so a wrong
 *                    auto-match is VISIBLE rather than silent.
 *  - `not_in_book` → marked row; nothing in the caller's book matched.
 *  - `search_failed` → marked row, distinct copy: the lookup itself errored, which
 *                    is not the same claim as "not in your portfolio". */
export const ASK_BACK_SLOT_STATE = {
    AMBIGUOUS: 'ambiguous',
    RESOLVED: 'resolved',
    NOT_IN_BOOK: 'not_in_book',
    SEARCH_FAILED: 'search_failed',
} as const;

export type AskBackSlotState = (typeof ASK_BACK_SLOT_STATE)[keyof typeof ASK_BACK_SLOT_STATE];

/** One name the user wrote, plus what the gate could make of it (v4 §4). */
export interface AskBackSlot {
    slot_id: string;
    /** The name as the user typed it — the label for this slot's group. */
    written: string;
    /** Widened past AskBackSlotState on purpose: an unknown state from a newer
     * Athena must degrade to a non-interactive marked row (see normaliseAskBackSlots),
     * never render as a required group nobody can satisfy and so wedge Confirm. */
    state: AskBackSlotState | string;
    /** `ambiguous` only. */
    candidates?: Candidate[];
    /** `resolved` only — what `written` matched. */
    org_id?: string;
    name?: string;
}

/** Runtime values for AskBackGate['kind'] — v4 §4 defines exactly two. */
export const ASK_BACK_KIND = {
    /** One or more names need choosing → the CompanyPicker form. */
    PICK: 'pick',
    /** A comparison was only half understood → prose only. Answered by typing a
     * normal message in the composer; there is NO reply shape and no picker. */
    CLARIFY: 'clarify',
} as const;

export type AskBackKind = (typeof ASK_BACK_KIND)[keyof typeof ASK_BACK_KIND];

export interface PickAskBackGate {
    kind: 'pick';
    prompt?: string;
    /** v4 §4 — one slot per written name. Absent on a pre-RECUR-27888 Athena,
     * which emits `candidates` alone; normaliseAskBackSlots reconciles the two so
     * no caller has to branch on which shape arrived.
     *
     * 🔴 This is the CLIENT PROJECTION, and it is deliberately the one we read.
     * Athena also carries a richer server-side `pending_slots` on the same turn
     * whose entries have extra fields (`confirm` on an ambiguous slot,
     * `company_row_count` on a not_in_book one) — measured by RECUR-27894
     * 2026-08-24. Do NOT switch a reader to `pending_slots` because a field there
     * looks useful: the two arrays diverge, and `confirm` in particular is exactly
     * the sort of field a renderer would reach for and would then be reading off a
     * shape nothing else in this repo consumes. */
    slots?: AskBackSlot[];
    /** LEGACY single-slot field, still emitted alongside `slots` when there is
     * exactly one ambiguous slot (v4 §5) — it is what keeps the shipped
     * production frontend working until this ticket deploys.
     *
     * 🔴 CONDITIONAL, and measured as such against the live dev book
     * (RECUR-27894, 2026-08-24): present when exactly ONE slot is ambiguous — even
     * alongside a `resolved` or `not_in_book` sibling — and **ABSENT** the moment
     * two are. So its absence is the signal for a genuine multi-slot form, and no
     * code path may treat it as always-present. normaliseAskBackSlots is the only
     * reader, and prefers `slots` whenever they exist. */
    candidates?: Candidate[];
}

/**
 * 🔴 SHAPE OBSERVED, PATH UNEXERCISED — and the two are not the same claim.
 *
 * RECUR-27894 (2026-08-24) drove the gate's clarify branch by injecting the
 * CLASSIFIER's decision and recorded what the gate emits, so the field names and
 * the turn-level values below are observed from the producer. What that does NOT
 * establish is reachability: **zero clarifies across 166 corpus records and 47
 * table cases.** Nobody has seen a user turn reach this state — session 2 built
 * it, RECUR-27894 has seen what it emits, and a dormant-path assertion reports
 * zero on every run and fires the moment that changes.
 *
 * So the tests covering this type are provenance-MIXED: observed shape, injected
 * trigger. They prove agreement with what the gate emits and assert nothing about
 * whether the state can occur. Do not read them as evidence the path is live.
 *
 * Observed turn-level values, all of which this repo relies on:
 *   outcome_type    = ask_back
 *   query_tag       = id_clarify   — deliberately OUTSIDE GATE_CONTROL_QUERY_SOURCES,
 *                                    which is what keeps the question in the
 *                                    transcript across a reload (it is the flag
 *                                    PortfolioAskAicaSurfaceProvider filters on)
 *   pending_slots   = null · candidate_map = {}   — nothing can drive a picker
 *   answer_scope    = null                        — no scope line can attach
 *   current_company = the prior pin, UNCHANGED    — a clarify asks, it never switches
 */
export interface ClarifyAskBackGate {
    kind: 'clarify';
    /** The operand the gate could not account for, e.g. "dashboard".
     *
     * DECLARED BUT NEVER RENDERED here: the user-facing wording is `prompt`, which
     * the server authors — including its empty-term fallback ("Which company do you
     * mean on the other side of that comparison?", emitted when `unresolved_term`
     * is present but an empty string). Kept on the type because it is on the wire
     * and a future affordance may want it; do not build copy out of it without
     * handling the empty case the server already handles. */
    unresolved_term: string;
    /** Rendered as a normal assistant bubble, not a card — a clarify is a turn in
     * the dialogue and the prompt IS the turn's whole content. Reaches the UI as
     * streamed message content (runTurn suppresses only `kind === "pick"`), not by
     * being read off this object. */
    prompt: string;
}

/** v4 §4 — `pick` (a form) or `clarify` (prose). Narrow on `kind` before
 * touching either one's own fields. */
export type AskBackGate = PickAskBackGate | ClarifyAskBackGate;

/** One answered slot, as sent back on gate_reply.selections (v4 §5). */
export interface GateSelection {
    slot_id: string;
    selected_org_id: string;
}

export interface DeclineGate {
    /** Human-readable reason string from Athena — rendered verbatim (see DeclineNotice). */
    reason: string;
}

export interface GateOutcome {
    type: GateOutcomeType;
    current_company: CurrentCompany | null;
    ask_back?: AskBackGate;
    decline?: DeclineGate;
}

export interface GlobalMessage {
    id: number;
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    /** Citation sources from Athena (final chunk only) — mirrors the leads
     * Message.metadata.sources shape so the reused CitationAnswer/SourcesPanel
     * render identically on the portfolio surface (RECUR-27369). */
    metadata?: {
        sources?: CitationSource[];
        /** Which companies this answer covered, and which written names it could
         * not (frozen contract v4 §7) — arrives on the final chunk beside
         * `sources`. Live-turn only: the conversation-history response does not
         * carry it, so the scope line does not survive a reload (known
         * limitation, ruled on by the architecture session 2026-08-23). */
        answer_scope?: AnswerScope;
    };
    /** Athena H-11: history items may flag a gate ask-back PROMPT turn (multi-
     * candidate "I found multiple companies matching…" AND confirm-single "Did
     * you mean X? Please confirm.") structurally, instead of the FE guessing
     * off English copy via ASK_BACK_PROMPT_PREFIX (which never covered the
     * confirm-single phrasing). Optional/additive — undefined on live turns
     * (never persisted, see runTurn) and on older history rows the BE hasn't
     * backfilled; the prefix match stays as a fallback for those. Decline
     * turns are NOT flagged — Q7 keeps decline text as a plain bubble. */
    is_gate_control?: boolean;
}

/** A row in the user-scoped conversation list (GET /chat/conversations/user). */
export interface GlobalConversationListItem {
    conversation_id: string;
    user_id: string;
    user_name: string | null;
    title: string | null;
    last_message_at: string | null;
}

/** A conversation the user has navigated away from while its turn is still streaming.
 * Holds exactly the three fields that are per-conversation, so switching back can
 * restore the live turn instead of re-fetching over the top of it. */
export interface BackgroundThread {
    messages: GlobalMessage[];
    isLoading: boolean;
    reasoning?: ReasoningStep;
}

export interface GlobalAskAicaState {
    /** The ACTIVE (on-screen) conversation's messages. Background conversations live in
     * `background` — see BackgroundThread. */
    messages: GlobalMessage[];
    isLoading: boolean;
    isLoadingChatHistory: boolean;
    error: string | null;
    /** Streamed reasoning for the in-flight turn — mirrors the leads
     * askAicaReducer.reasoning shape so the reused ReasoningPanel works as-is. */
    reasoning?: ReasoningStep;

    /** The company the gate has currently resolved (or null when company-less). */
    currentCompany: CurrentCompany | null;
    /** True once the gate has explicitly told us this turn is company-less. */
    companyLess: boolean;
    /** Present only while an ask_back gate is open; null once resolved/declined. */
    askBack: AskBackGate | null;
    /** Present only while a decline gate is open. */
    decline: DeclineGate | null;

    /** Held across turns once captured from the stream; omitted on the very first turn. */
    conversationId: string | null;

    conversations: GlobalConversationListItem[];
    isLoadingConversations: boolean;

    selectedSources: string[];

    /** True while "Delete Chat" is clearing the current conversation's memory (F-7). */
    isClearingChat: boolean;
    /** True when the last loaded history page was full — older messages may exist (F-9). */
    hasMoreData: boolean;

    /** True from New Chat click until the next turn's first conversation_id
     * lands (D1/D2) — see actions.ts::setPendingNewConversation. */
    pendingNewConversation: boolean;

    /** Turns still streaming in conversations the user has navigated away from, keyed by
     * conversation id (or DRAFT_CONVERSATION_KEY). Populated on switch-away, drained on
     * switch-back, so a response keeps building while you read something else. */
    background: Record<string, BackgroundThread>;
}
